import EverTrue from 'app';
import { useState, useCallback } from 'react';
import { pollAnimatedThumbnailUntilFinished, uploadVideoHelper } from 'entities/helpers/thankview-helper';
import { useThankViewAuth } from 'apps/signal-video/contexts/thankview-auth-context';

const useVideoProcessing = (video_id = null) => {
  const [abortController, setAbortController] = useState(null);
  const [processingVideoState, setProcessingVideoState] = useState('');
  const { getValidToken } = useThankViewAuth();

  const uploadVideoFromFile = useCallback(
    async (video, fileName, duration, personalVideoParams = {}) => {
      setProcessingVideoState('uploading');
      try {
        const formData = new FormData();
        formData.append('file', video);
        formData.append('video_length', Math.round(duration).toString());
        formData.append('personalVideoParams', JSON.stringify(personalVideoParams));
        formData.append('localDate', new Date().toISOString().split('T')[0]);
        const token = await getValidToken();
        const { libraryVideo, personalVideo } = await uploadVideoHelper(formData, token);
        setProcessingVideoState('generating_thumbnail');
        const { animatedThumbnailPath } = await pollAnimatedThumbnailUntilFinished(libraryVideo.id, token);
        libraryVideo.animated_thumb_path = animatedThumbnailPath;
        EverTrue.Alert.success('Video uploaded successfully');
        return { ...personalVideo, libraryVideo };
      } catch (error) {
        EverTrue.Alert.error('There was a problem uploading your video');
        throw error;
      } finally {
        setProcessingVideoState('');
      }
    },
    [getValidToken]
  );

  const uploadVideo = useCallback(
    async (blob, fileName, duration, personalVideoParams = {}) => {
      setProcessingVideoState('uploading');
      // Cancel previous request if it exists
      if (abortController) {
        abortController.abort();
      }
      const newController = new AbortController();
      setAbortController(newController);
      try {
        const isChrome = /Chrome/.test(navigator.userAgent) && !/Chromium|Edge|Edg/.test(navigator.userAgent);
        const mimeType = blob.type.toLowerCase();
        const baseMimeType = mimeType.split(';')[0];
        let fileExtension = 'mp4';

        if (isChrome && baseMimeType.includes('matroska')) {
          fileExtension = 'webm';
        } else if (baseMimeType.includes('webm')) {
          fileExtension = 'webm';
        } else if (baseMimeType.includes('quicktime')) {
          fileExtension = 'mov';
        } else if (baseMimeType.includes('matroska')) {
          fileExtension = 'mkv';
        }

        const actualFileName = `video_${Date.now()}.${fileExtension}`;
        const videoFile = new File([blob], actualFileName, { type: blob.type });
        return await uploadVideoFromFile(videoFile, actualFileName, duration, personalVideoParams);
      } catch (error) {
        EverTrue.Alert.error('There was a problem uploading your video');
        throw error;
      } finally {
        setProcessingVideoState('');
      }
    },
    [abortController, uploadVideoFromFile]
  );

  return { uploadVideo, uploadVideoFromFile, processingVideoState };
};

export default useVideoProcessing;
